import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { AppNav } from '@/components/AppNav';
import { updateSettings, type User } from '@/lib/api';
import { sessionKey, useLogout, useSession } from '@/hooks/useSession';
import { useT } from '@/i18n/context';
import { translateError } from '@/i18n/errors';
import { LOCALES, type Locale } from '@/i18n/locale';
import type { MessageKey } from '@/i18n/types';
import { currentEndpoint, disablePush, enablePush, pushState } from '@/lib/push';
import { detectTimeZone } from '@/lib/dates';

const CHANNELS: {
  value: User['notification_channel'];
  label: MessageKey;
  hint: MessageKey;
}[] = [
  { value: 'both', label: 'settings.channel_both', hint: 'settings.channel_both_hint' },
  { value: 'push', label: 'settings.channel_push', hint: 'settings.channel_push_hint' },
  {
    value: 'email',
    label: 'settings.channel_email',
    hint: 'settings.channel_email_hint',
  },
  { value: 'off', label: 'settings.channel_off', hint: 'settings.channel_off_hint' },
];

/**
 * Each language named in itself, never translated. Someone who has landed in a
 * language they cannot read needs to recognise their own in the list.
 */
const LANGUAGE_NAMES: Record<Locale, string> = {
  en: 'English',
  ru: 'Русский',
};

export function SettingsPage() {
  const queryClient = useQueryClient();
  const t = useT();
  const { data: user } = useSession();
  const signOut = useLogout();
  const [pushError, setPushError] = useState<string | null>(null);
  const [time, setTime] = useState(user?.notification_time ?? '20:00');

  const { data: endpoint, refetch: refetchEndpoint } = useQuery({
    queryKey: ['push', 'endpoint'],
    queryFn: currentEndpoint,
  });

  const save = useMutation({
    mutationFn: updateSettings,
    onSuccess: ({ user: updated }) => queryClient.setQueryData(sessionKey, updated),
  });

  const togglePush = useMutation({
    mutationFn: async (enable: boolean) => (enable ? enablePush() : disablePush()),
    onSuccess: () => {
      setPushError(null);
      void refetchEndpoint();
    },
    onError: (error: Error) =>
      setPushError(translateError(t, error, 'errors.push_failed')),
  });

  const permission = pushState();
  const subscribed = Boolean(endpoint);
  const zone = detectTimeZone();

  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col gap-6 p-6 pb-24">
      <header>
        <h1 className="text-3xl font-semibold tracking-tight">{t('settings.title')}</h1>
        <p className="text-sm opacity-60">{user?.email}</p>
      </header>

      {/* Saved like every other setting rather than kept on the device: it is
          also the language the reminder emails are written in, and those are
          rendered on the queue worker where no browser is present. */}
      <section className="flex flex-col gap-2">
        <h2 className="text-sm font-medium">{t('settings.language')}</h2>

        {LOCALES.map((option) => (
          <label
            key={option}
            className="flex items-center gap-3 rounded-lg bg-current/5 p-3 text-sm"
          >
            <input
              type="radio"
              name="language"
              checked={user?.language === option}
              onChange={() => save.mutate({ language: option })}
            />
            <span>{LANGUAGE_NAMES[option]}</span>
          </label>
        ))}

        <p className="text-xs opacity-50">{t('settings.language_hint')}</p>
      </section>

      <section className="flex flex-col gap-3">
        <label htmlFor="reminder-time" className="text-sm font-medium">
          {t('settings.remind_at')}
        </label>

        {/* Saved on blur, not on change: a time input emits an event for every
            segment the user moves through, so saving on change would fire a
            request per keystroke and store half-typed times along the way. */}
        <input
          id="reminder-time"
          type="time"
          value={time}
          onChange={(event) => setTime(event.target.value)}
          onBlur={() => {
            if (time && time !== user?.notification_time) {
              save.mutate({ notification_time: time });
            }
          }}
          className="rounded-lg bg-current/5 px-4 py-3"
        />

        {/* The time is wall-clock in the user's own zone, so which zone that is
            has to be visible — otherwise "20:00" is ambiguous after travelling. */}
        <p className="text-xs opacity-50">
          {zone}
          {user && user.timezone !== zone && (
            <>
              {' · '}
              <button
                type="button"
                onClick={() => save.mutate({ timezone: zone })}
                className="underline"
              >
                {t('settings.update_timezone', { zone: user.timezone })}
              </button>
            </>
          )}
        </p>
      </section>

      <section className="flex flex-col gap-2">
        <h2 className="text-sm font-medium">{t('settings.how')}</h2>

        {CHANNELS.map((channel) => (
          <label
            key={channel.value}
            className="flex items-start gap-3 rounded-lg bg-current/5 p-3 text-sm"
          >
            <input
              type="radio"
              name="notification_channel"
              className="mt-1"
              checked={user?.notification_channel === channel.value}
              onChange={() => save.mutate({ notification_channel: channel.value })}
            />
            <span>
              {t(channel.label)}
              <span className="block text-xs opacity-50">{t(channel.hint)}</span>
            </span>
          </label>
        ))}
      </section>

      {permission !== 'unsupported' && (
        <section className="flex flex-col gap-2">
          <h2 className="text-sm font-medium">{t('settings.this_device')}</h2>

          <button
            type="button"
            disabled={togglePush.isPending || permission === 'denied'}
            onClick={() => togglePush.mutate(!subscribed)}
            className="rounded-full bg-current/10 px-6 py-3.5 font-medium disabled:opacity-40"
          >
            {subscribed ? t('settings.push_disable') : t('settings.push_enable')}
          </button>

          {permission === 'denied' && (
            <p className="text-xs opacity-50">{t('settings.push_denied')}</p>
          )}
        </section>
      )}

      {permission === 'unsupported' && (
        <p className="rounded-lg bg-current/5 p-4 text-sm opacity-70">
          {t('settings.push_unsupported')}
        </p>
      )}

      {pushError && (
        <p role="alert" className="rounded-lg bg-red-500/10 p-4 text-sm text-red-500">
          {pushError}
        </p>
      )}

      {save.isError && (
        <p role="alert" className="rounded-lg bg-red-500/10 p-4 text-sm text-red-500">
          {translateError(t, save.error, 'errors.save_failed')}
        </p>
      )}

      <button
        type="button"
        onClick={() => signOut.mutate()}
        className="text-sm underline opacity-60"
      >
        {t('common.sign_out')}
      </button>

      <AppNav />
    </main>
  );
}
